<script setup>
import { setErrors } from '@formkit/core'

// Props
const props = defineProps({
  userList: {
    type: Object,
    default: null,
  },
  onSave: {
    type: Function,
    default: () => {},
  },
  title: {
    type: String,
    default: 'Create a List',
  },
  submitLabel: {
    type: String,
    default: 'Save List',
  },
  actions: {
    type: Array,
    default: () => [],
  },
})

const { $auth } = useNuxtApp()
const defaultFormData = {
  name: '',
  description: '',
  creator: `${$auth.user.first_name} ${$auth.user.last_name}`,
}
const formData = ref({})

const emit = defineEmits(['clearErrors', 'saved', 'closed'])

// Populates form in create or edit mode
watch(
  () => props.userList,
  (newData) => {
    if (newData && Object.keys(newData).length > 0) {
      formData.value = newData
    } else {
      formData.value = { ...defaultFormData }
    }
  },
  { immediate: true, deep: true },
)

async function save() {
  try {
    await props.onSave(formData)
    onClosed()
    emit('saved')
  } catch (e) {
    if (e.response && e.response.status === 400) {
      const errors = {}
      for (const [key, value] of Object.entries(e.response.data)) {
        errors[key] = value.map((x) => `Error: ${x}`)
      }
      setErrors('userlist-form', errors)
    } else {
      throw e
    }
  }
}

const clearInput = () => {
  formData.value = defaultFormData
}

function onClosed() {
  clearInput()
  emit('closed')
}

defineExpose({
  clearInput,
  save,
})
</script>

<template>
  <div class="user-list-form">
    <div class="close-btn-wrapper">
      <h3>{{ title }}</h3>
      <div class="close-btn">
        <button
          role="button"
          aria-label="Close"
          @keydown.esc="onClosed()"
          @click="onClosed()"
        >
          &#x2715;
        </button>
      </div>
    </div>
    <FormKit
      id="userlist-form"
      v-model="formData"
      type="form"
      :actions="false"
      @submit="save"
    >
      <span class="field-counter name-count">
        {{ (formData.name || '').length }} / 36
      </span>
      <FormKit
        label="Name"
        type="text"
        name="name"
        validation="required:trim|length:1,36"
      ></FormKit>
      <span class="field-counter descrption-count">
        {{ (formData.description || '').length }} / 500
      </span>
      <FormKit
        label="Description (optional)"
        type="textarea"
        name="description"
        validation="length:0,500"
        :validation-messages="{
          length: 'Error: Description must be shorter than 500 characters',
        }"
      ></FormKit>
      <span class="field-counter creator-count">
        {{ (formData.creator || '').length }} / 36
      </span>
      <FormKit
        label="Your Name (customizable)"
        type="text"
        name="creator"
        validation="required:trim|length:1,36"
        :validation-messages="{
          required: 'Error: Your Name is required',
          length: 'Error: Your Name must be shorter than 36 characters',
        }"
      ></FormKit>
    </FormKit>

    <div class="formkit-actions">
      <button
        v-for="(action, index) in actions"
        :key="index"
        type="button"
        :class="action.class"
        @click="action.handler"
      >
        {{ action.label }}
      </button>

      <button type="submit" form="userlist-form" class="formkit-input">
        {{ props.submitLabel }}
      </button>
    </div>
  </div>
</template>
